import { createTheme, useTheme } from "@mui/material";

const neutral = "#75777A";
const neutralDark = "#1A1C1E";
const gray1 = "#C1C7CE";
const primary = {
  light: "#C9E6FF",
  dark: "#004B6F",
  main: "#8ACEFF",
  contrastText: "#00344E",
};

declare module "@mui/material/styles" {
  interface TypographyVariants {
    titleLarge: React.CSSProperties;
    titleMedium: React.CSSProperties;
    titleSmall: React.CSSProperties;
    bodyMedium: React.CSSProperties;
    weatherData: React.CSSProperties;
    labelLarge: React.CSSProperties;
    labelMedium: React.CSSProperties;
    bodySmall: React.CSSProperties;
  }

  // allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    titleLarge?: React.CSSProperties;
    titleMedium?: React.CSSProperties;
    titleSmall?: React.CSSProperties;
    bodyLarge?: React.CSSProperties;
    bodyMedium?: React.CSSProperties;
    weatherData?: React.CSSProperties;
    labelLarge?: React.CSSProperties;
    labelMedium?: React.CSSProperties;
    bodySmall?: React.CSSProperties;
  }
}

// Update the Typography's variant prop options
declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    titleLarge: true;
    titleMedium: true;
    titleSmall: true;
    bodyLarge: true;
    bodyMedium: true;
    weatherData: true;
    labelLarge: true;
    labelMedium: true;
    bodySmall: true;
  }
}

declare module "@mui/material/styles/createPalette" {
  interface PaletteOptions {
    neutral: string;
    neutralDark: string;
    gray1: string;
    yellow?: ColorPartial;
    orange?: ColorPartial;
    red?: ColorPartial;
    rose?: ColorPartial;
    pink?: ColorPartial;
    fuchsia?: ColorPartial;
    violet?: ColorPartial;
    indigo?: ColorPartial;
    blue?: ColorPartial;
    cyan?: ColorPartial;
    slate?: ColorPartial;
    stone?: ColorPartial;
    teal?: ColorPartial;
    emerald?: ColorPartial;
    green?: ColorPartial;
    lime?: ColorPartial;
    routeStates?: {
      planned: string;
      ended: string;
      live: string;
      liveDarker: string;
      selectedBorder: string;
      selectedColor: string;
      selectedDarker: string;
    };
  }

  interface Palette {
    neutral: string;
    neutralDark: string;
    gray1: string;
    yellow: ColorPartial;
    orange: ColorPartial;
    red: ColorPartial;
    rose: ColorPartial;
    pink: ColorPartial;
    fuchsia: ColorPartial;
    violet: ColorPartial;
    indigo: ColorPartial;
    blue: ColorPartial;
    cyan: ColorPartial;
    slate: ColorPartial;
    stone: ColorPartial;
    teal: ColorPartial;
    emerald: ColorPartial;
    green: ColorPartial;
    lime: ColorPartial;
    routeStates: {
      planned: string;
      ended: string;
      live: string;
      liveDarker: string;
      selectedBorder: string;
      selectedColor: string;
      selectedDarker: string;
    };
  }
}

export const darkTheme = createTheme({
  palette: {
    mode: "dark",
    background: {
      default: "#111416",
      paper: "#1E2022",
    },
    primary,
    error: { main: "#FF897D" },
    success: { main: "#60DBB9" },
    warning: { main: "#DFCC87" },
    info: { main: "#E57DFF" },
    grey: {
      50: "#E2E2E5",
      100: "#C5C6C9",
      200: "#C1C7CE",
      300: "#8B9198",
      400: "#41474D",
      500: "#333537",
      600: "#282A2D",
      700: "#1E2022",
      800: "#1A1C1E",
      900: "#111416",
    },
    yellow: {
      300: "#F1D07E",
      400: "#FDCB08",
      500: "#DEAA08",
      600: "#CA8A04",
      700: "#A16207",
    },
    orange: {
      300: "#FDBA74",
      400: "#FB8A2D",
      500: "#F96D0B",
      600: "#EA580C",
      700: "#C2410C",
    },
    red: {
      300: "#FCAFA5",
      400: "#FE705D",
      500: "#EF3E3E",
      600: "#DC2626",
      700: "#B91C1C",
    },
    rose: {
      300: "#FDA4AF",
      400: "#FD5E75",
      500: "#F43959",
      600: "#E11D48",
      700: "#BE123C",
    },
    pink: {
      300: "#F9A8D4",
      400: "#F368B1",
      500: "#EB4295",
      600: "#DB2777",
      700: "#BE185D",
    },
    fuchsia: {
      300: "#F0ABFC",
      400: "#EA6CFE",
      500: "#D843EF",
      600: "#C026D3",
      700: "#A21CAF",
    },
    violet: {
      300: "#C4B5FD",
      400: "#9F80FA",
      500: "#8756F6",
      600: "#7C3AED",
      700: "#6D28D9",
    },
    indigo: {
      300: "#A5B4FC",
      400: "#7783F8",
      500: "#6063F1",
      600: "#4F46E5",
      700: "#4338CA",
    },
    blue: {
      300: "#93C5FD",
      400: "#57A0FA",
      500: "#3780F6",
      600: "#2563EB",
      700: "#1D4ED8",
    },
    cyan: {
      300: "#77E5F8",
      400: "#19C4E1",
      500: "#16A7C0",
      600: "#148AA6",
      700: "#0E7490",
    },
    teal: {
      300: "#81EEDD",
      400: "#16CAB4",
      500: "#14B3A2",
      600: "#0D9488",
      700: "#0F766E",
    },
    emerald: {
      300: "#6EE7B7",
      400: "#2BCA90",
      500: "#0FAE79",
      600: "#058F64",
      700: "#047857",
    },
    green: {
      300: "#86EFAC",
      400: "#3CD775",
      500: "#25B65B",
      600: "#1E944A",
      700: "#147B3A",
    },
    lime: {
      300: "#C6F47B",
      400: "#A1D945",
      500: "#7EBA21",
      600: "#63961A",
      700: "#4D7C0F",
    },
    stone: {
      300: "#B7B2AE",
      400: "#9F9893",
      500: "#78716C",
      600: "#57534E",
      700: "#44403C",
    },
    slate: {
      300: "#9EB1C7",
      400: "#8496AE",
      500: "#64748B",
      600: "#475569",
      700: "#334155",
    },
    routeStates: {
      planned: "#58B4EF",
      ended: "#8F9193",
      live: "#3FBF9E",
      liveDarker: "#00382B", // Darker version of live for selected state
      selectedBorder: "#E2E2E5",
      selectedColor: "#1A1C1E",
      selectedDarker: "#6B6D6F", // Darker version of ended for selected state
    },
    neutral,
    neutralDark,
    gray1,
  },
  typography: {
    fontFamily: [
      "Inter",
      "-apple-system",
      "BlinkMacSystemFont",
      '"Segoe UI"',
      "Roboto",
      '"Helvetica Neue"',
      "Arial",
      "sans-serif",
      '"Apple Color Emoji"',
      '"Segoe UI Emoji"',
      '"Segoe UI Symbol"',
    ].join(","),
    titleLarge: {
      fontWeight: "400",
      fontSize: "22px",
    },
    titleMedium: {
      fontWeight: "500",
      fontSize: "16px",
      lineHeight: "24px",
    },
    titleSmall: {
      fontSize: "14px",
      fontWeight: "500",
      lineHeight: "20px",
    },
    bodyLarge: {
      fontWeight: "400",
      fontSize: "16px",
      lineHeight: "24px",
    },
    bodyMedium: {
      fontWeight: "400",
      fontSize: "14px",
    },
    body1: {
      fontWeight: "500",
      fontSize: "14px",
    },
    body2: {
      fontWeight: "600",
      fontSize: "12px",
    },
    subtitle1: {
      fontWeight: "700",
    },
    subtitle2: {
      fontWeight: "400",
    },
    weatherData: {
      fontSize: "28px",
      fontWeight: "600",
      lineHeight: "36px",
      letterSpacing: "0em",
      textAlign: "left",
      color: "#8ACEFF",
    },
    labelLarge: {
      fontWeight: "600",
      fontSize: "14px",
      lineHeight: "20px",
    },
    labelMedium: {
      fontWeight: "500",
      fontSize: "12px",
      lineHeight: "16px",
    },
    bodySmall: {
      fontWeight: "400",
      fontSize: "12px",
      lineHeight: "16px",
    },
  },
  components: {
    MuiToolbar: {
      styleOverrides: {
        root: {
          minHeight: "56px",
          "@media (min-width: 600px)": {
            minHeight: "56px",
          },
          "@media (min-width: 0px)": {
            "@media (orientation: landscape)": {
              minHeight: "56px",
            },
          },
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          borderRadius: "12px",
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: "6px 24px",
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          "&:last-child th": {
            borderBottom: 0,
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
        },
        outlined: {
          borderRadius: "200px",
          textTransform: "none",
        },
        contained: {
          borderRadius: "200px",
          textTransform: "none",
          backgroundColor: primary.main,
          color: primary.contrastText,

          "&:hover": {
            boxShadow:
              "0px 1px 3px 1px rgba(0, 0, 0, 0.15), 0px 1px 2px 0px rgba(0, 0, 0, 0.30)",
            backgroundColor: primary.main,
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          "&:hover": {
            backgroundColor: "transparent",
          },
          "&.Mui-focused": {
            backgroundColor: "transparent", // Remove the focused background color
          },
          "&.Mui-error": {
            backgroundColor: "transparent", // Remove the error background color
          },
          background: "transparent",
          color: gray1,
          "&.Mui-disabled": {
            borderRadius: "200px", // Adjust this value as needed
          },
        },
        filled: {
          borderRadius: "200px", // Adjust this value as needed
          padding: "8px 16px",
          backgroundColor: "#282A2D",
          color: gray1,
        },
        icon: {
          right: "8px",
        },
        standard: {
          "&:hover": {
            backgroundColor: "transparent",
          },
          "&.Mui-focused": {
            backgroundColor: "transparent", // Remove the focused background color
          },
          "&.Mui-error": {
            backgroundColor: "transparent", // Remove the error background color
          },
          background: "transparent",
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        selectRoot: {
          fontWeight: 400,
        },
        selectLabel: {
          fontWeight: 400,
        },
        select: {
          fontWeight: 400,
        },
        displayedRows: {
          fontWeight: 400,
        },
      },
    },
    MuiBreadcrumbs: {
      styleOverrides: {
        root: {
          marginTop: "24px",
          marginBottom: "40px",
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: "none",
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          position: "absolute",
          bottom: "-20px",
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          padding: "0px",
          mb: "16px",
        },
      },
    },
    MuiDialogContentText: {
      styleOverrides: {
        root: {
          padding: "0px",
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: "0px",
          mb: "16px",
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: "28px",
          padding: "24px",
          backgroundColor: "#282A2D",
          backgroundImage: "none",
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: "8px",
        },
        label: {
          padding: "4px 8px",
        },
        outlined: {
          borderColor: "#8B9198",
        },
      },
    },
  },
});

export type AppTheme = typeof darkTheme;

export const useAppTheme = () => useTheme<AppTheme>();
